// Sentry (errors) + PostHog (product analytics) — go-public observability.
// PII-HARDENED: this app handles SSNs and client financial data, so
//  - Sentry: no default PII, no session replay, request bodies never attached.
//  - PostHog: all session-recording text and inputs are MASKED before leaving
//    the browser; autocapture never captures input values by design.
// The DSN and phc token are public client-side identifiers (safe to ship).
import * as Sentry from "@sentry/react";
import posthog from "posthog-js";

const SENTRY_DSN = "https://d50c6d8a48bd1ec915007d377689bf81@o4511768925175808.ingest.us.sentry.io/4511768947130368";
const POSTHOG_TOKEN = "phc_u2QL4CAkxMSKCd6wBcMez2F8vcREi8fDEhuoAYBvF5Gd"; // shared "No Excuses Apps" project — filter by app tag
const APP_TAG = "dispute-manager";

// Routes that carry a LIVE credential in the URL itself. A password-reset,
// staff-invite, company-invite, portal set-password, intake or filing-review
// link IS the authentication — whoever holds the URL can act as that person
// until it expires. Neither PostHog nor Sentry may ever receive one: their
// read access is far broader than the app's, and the PostHog project is shared
// across Key's apps. Scrub the token out of every URL-shaped field before
// either SDK transmits it.
const TOKEN_PATH = /(\/(reset-password|staff-invite|join|filing|intake|portal\/set-password)\/)[^/?#]+/gi;
const TOKEN_QUERY = /([?&](?:token|code|t)=)[^&#]+/gi;

export function redactUrl(value: string): string {
  return value.replace(TOKEN_PATH, "$1<redacted>").replace(TOKEN_QUERY, "$1<redacted>");
}

function scrubDeep(obj: unknown, depth = 0): void {
  if (!obj || typeof obj !== "object" || depth > 4) return;
  for (const [k, v] of Object.entries(obj as Record<string, unknown>)) {
    if (typeof v === "string" && (v.includes("/") || v.includes("token="))) {
      (obj as Record<string, unknown>)[k] = redactUrl(v);
    } else if (typeof v === "object") {
      scrubDeep(v, depth + 1);
    }
  }
}

export function initObservability() {
  if (typeof window === "undefined") return;
  if (/^(localhost|127\.|192\.168\.)/.test(window.location.hostname)) return; // dev: stay silent

  Sentry.init({
    dsn: SENTRY_DSN,
    sendDefaultPii: false,
    tracesSampleRate: 0.2,
    initialScope: { tags: { app: APP_TAG } },
    // Scrub before send: the URL rides on the event itself, its request
    // context, and every breadcrumb/span — so walk the whole payload rather
    // than patching one field.
    beforeSend(event) {
      scrubDeep(event);
      return event;
    },
    beforeBreadcrumb(crumb) {
      scrubDeep(crumb);
      return crumb;
    },
  });

  posthog.init(POSTHOG_TOKEN, {
    api_host: "https://us.i.posthog.com",
    person_profiles: "identified_only",
    capture_pageview: true,
    autocapture: true,
    session_recording: {
      maskAllInputs: true,
      maskTextSelector: "*", // never ship visible SSNs/report data in replays
    },
    // $current_url / $pathname / referrer are captured on every pageview and
    // autocapture event — redact the token out of all of them.
    sanitize_properties: (props) => {
      for (const k of ["$current_url", "$pathname", "$referrer", "$initial_current_url"]) {
        if (typeof props[k] === "string") props[k] = redactUrl(props[k] as string);
      }
      return props;
    },
  });
  posthog.register({ app: APP_TAG });
}
